<template>
<div class="row">

	<div class="col">
		<b-form @submit.prevent="submitForm"
		        class="mb-3">

			<action-buttons ref="actionButtons"
			                class="pull-right"
			                @save="submitForm"
			                @save-and-back="submitFormAndBack"
			                @back="goBack"
			                @delete="deletePendingBid"
			                :disable-next="true"
			                :disable-previous="true"
			                :disable-save-and-new="true"
			                :disable-delete="mode == 'add'"
			                :isDeleting="isDeleting"
			                delete-name="dit voorlopige bod">
			</action-buttons>

			<h1 class="display-4"
			    v-if="mode == 'edit'">{{ $t('pendingBid.editTitle') }}</h1>

			<h1 class="display-4"
			    v-if="mode == 'add'">{{ $t('pendingBid.addTitle') }}</h1>

			<b-alert variant="warning"
			         show
			         v-if="formError.errors">

				<span v-for="errors in formError.errors">

					<span v-for="error in errors">
						{{ error }}
						<br />
					</span>

				</span>

			</b-alert>

			<partial-loader :loading="isLoading"></partial-loader>

			<div v-if="!isLoading">
				<b-jumbotron class="mb-3 p-4">
					<h3 v-if="item">{{ $t('pendingBid.pendingBidOnItem') }}
						<router-link :to="{ name: 'item', params: { itemId: item.id } }">
							<strong>{{ item.seller.code }}-{{ item.number }}</strong> {{ item.title }}.
						</router-link>
					</h3>
					<h4 v-if="item">
						{{ `${$t('pendingBid.auctionOrderNumberSmall')} ${item.auction_order_number}` }}
					</h4>

					<p>
						<small v-if="mode == 'edit'">Aangemaakt op {{ bid.created_at | bigDate }}</small>
					</p>


					<h2 v-if="bid.pending_bid_number"
					    class="font-italic">
						{{ $t('bid.bidNumber') }} {{ bid.pending_bid_number }}
					</h2>

					<person-info :person="bid.buyer"></person-info>

				</b-jumbotron>

				<div class="row">
					<div class="col-sm-12 col-md-8 col-lg-6">
						<b-form-group :label="$t('pendingBid.price')">
							<b-input-group>
								<b-input-group-prepend is-text>
									&euro;
								</b-input-group-prepend>

								<b-form-input type="number"
								              step="1"
								              class="text-right"
											  autocomplete="off"
								              v-model="form.price"></b-form-input>
							</b-input-group>
						</b-form-group>
					</div>
				</div>

				<div class="row">
					<div class="col-sm-12 col-md-8 col-lg-6">
						<autocomplete-person :selected="(this.form.buyer_id) ? this.form.buyer_id : bid.buyer"
						                     :label="$t('common.buyer')"
						                     @update:person="buyerIsUpdated">
						</autocomplete-person>
					</div>
				</div>

				<div class="row" v-if="mode == 'add'">
					<div class="col-sm-12 col-md-8 col-lg-6">
						<b-form-group :label="$t('pendingBid.bidNumbers')">
							<b-form-select v-model="bidNumberTypeSelection.selection"
								:options="bidNumberTypeSelection.options" />
						</b-form-group>

						<b-form-group :label="$t('pendingBid.selectBidNumber')" v-if="bidNumberTypeSelection.selection == 'existing'">
							<b-form-select v-model="bidNumberSelection.selection"
								:options="bidNumberSelection.options" />
						</b-form-group>
					</div>
				</div>
			</div>
		</b-form>
	</div>
</div>
</template>

<script>
// imports
import AutocompletePerson from '@/components/elements/AutocompletePerson';
import ActionButtons from '@/components/elements/Edit/ActionButtons';
import PartialLoader from '@/components/elements/PartialLoader';
import PersonInfo from '@/components/views/Person/PersonInfo';
import { flashSuccess, flashDanger } from '@/helpers/flash-message-helper';
import { pick, isNil } from 'lodash';
import { replaceEmptyByNull } from '@/helpers/object-helper';
import { createNamespacedHelpers } from 'vuex';
import { bigDate } from '@/filters/date-filters';

// define variables
const { mapGetters, mapActions } = createNamespacedHelpers('pendingBidForm');

export default {
	components: {
		AutocompletePerson,
		ActionButtons,
		PartialLoader,
		PersonInfo
	},
	filters: {
		bigDate
	},
	data() {
		return {
			mode: 'add',
			bidNumberTypeSelection: {
				options: [
					{ value: 'new', text: 'Genereer schriftelijk biednummer' },
				],
				selection: 'new',
			},
			bidNumberSelection: {
				options: [],
				selection: null,
			},
			form: {
			},
		}
	},
	computed: {
		...mapGetters([
			'bid',
			'item',
			'bidNumbers',
			'isFetching',
			'isCreating',
			'isUpdating',
			'isDeleting',
			'formError',
			'deleteError'
		]),
		isLoading() {
			return (this.isFetching || this.isUpdating || this.isCreating);
		}
	},
	watch: {
		'$route': 'initialize',
		'bid': 'fillForm'
	},
	methods: {
		...mapActions({
			getPendingBid: 'getBid',
			getItem: 'getItem',
			createPendingBid: 'createBid',
			updatePendingBid: 'updateBid',
			deletePendingBidAction: 'deleteBid',
			getPersonBidNumbers: 'getPersonBidNumbers',
			resetAll: 'resetAll'
		}),

		/**
		 * Initialize the component
		 */
		initialize() {
			// reset form
			this.form = {};

			this.resetBidNumberData();
			this.resetAll();

			if (!isNil(this.$route.params.pendingBidId)) {
				this.mode = 'edit';
				this.getPendingBid(this.$route.params);
			} else {
				this.getItem(this.$route.params);
			}
		},

		/**
		 * Fill form with the initial bid values
		 */
		fillForm() {
			this.form = pick(this.bid, ['price', 'buyer_id']);
			this.form.auctionId = parseInt(this.$route.params.auctionId);

			if (!isNil(this.form.price))
				this.form.price = Math.round(this.form.price);
		},

		/**
		 * Buyer is updated
		 * @param  {Object} buyer person object with buyer_id
		 */
		buyerIsUpdated(buyer) {
			if (isNil(buyer)) {
				this.form.buyer_id = null;
			} 
			else {
				this.form.buyer_id = buyer.id;
			}

			this.setBidNumbers(buyer);
		},

		resetBidNumberData() {
			this.bidNumberTypeSelection = {
				options: [
					{ value: 'new', text: 'Genereer schriftelijk biednummer' },
				],
				selection: 'new',
			},

			this.bidNumberSelection = {
				options: [],
				selection: null,
			};
		},

		async setBidNumbers(buyer) {
			if (isNil(buyer)) {
				this.resetBidNumberData();
			}
			else {
				if (buyer.fixed_bid_number) {
					this.bidNumberSelection.options.push({
						value: buyer.fixed_bid_number, text: `${buyer.fixed_bid_number} (vast)`,
					})
				}

				await this.getPersonBidNumbers({ auctionId: this.form.auctionId, params: { person_id: this.form.buyer_id } })
				
				this.bidNumbers.forEach(bidNumber => {
					this.bidNumberSelection.options.push({
						value: bidNumber, text: `${bidNumber} (gegenereerd)`,
					})
				})

				if (this.bidNumberSelection.options.length > 0) {
					this.bidNumberTypeSelection.options.push(
						{ value: 'existing', text: "Gebruik bestaand biednummer" }
					);

					this.bidNumberTypeSelection.selection = 'existing';
					this.bidNumberSelection.selection = this.bidNumberSelection.options[0].value;
				}
			}
		},

		/**
		 * Delete pending bid and go to pending bid list
		 */
		deletePendingBid() {
			this.deletePendingBidAction({
					itemId: this.bid.item.id,
					pendingBidId: this.bid.id
				})
				.then(() => {
					if (!this.deleteError) {
						flashSuccess(this.$t('pendingBid.deleteSuccess'), true);
						this.$router.push({ name: 'pendingBidList' });
					} else {
						this.$refs.actionButtons.hideDeleteModal();
						flashDanger(this.$t('bid.deleteError'));
					}
				});
		},

		/**
		 * Submit Form
		 * @return {Promise} promise is resolved when bid is updated
		 * @param 	{event} 	e 			submit event
		 * @param 	{Boolean}	nextPage	should the success message show on the next page
		 */
		submitForm(e, nextPage = false) {
			if (this.mode == 'edit') {
				return this.updatePendingBid({
					itemId: this.item.id,
					pendingBidId: this.bid.id,
					bid: replaceEmptyByNull(this.form)
				}).then(() => {
					if (!this.formError.errors) {
						flashSuccess(this.$t('pendingBid.updateSuccess'), nextPage);
						if (nextPage)
							this.goBack();
					}
				});
			} else if (this.mode == 'add') {
				
				if (this.bidNumberTypeSelection.selection == 'existing')
					this.form.bid_number = this.bidNumberSelection.selection;
				
				return this.createPendingBid({
					itemId: this.item.id,
					bid: replaceEmptyByNull(this.form),
				}).then(() => {
					if (!this.formError.errors) {
						flashSuccess(this.$t('pendingBid.createSuccess'), true);
						if (nextPage) {
							this.goBack();
						} else {
							this.$router.push({ name: 'pendingBidEdit', params: { pendingBidId: this.bid.id }, query: this.$route.query });
						}
					}
				});
			}
		},

		/**
		 * Submit the form and go back to the bid list
		 */
		submitFormAndBack() {
			this.submitForm(null, true);
		},

		/**
		 * Go back to the bid list
		 */
		goBack() {
			let backRoute = 'pendingBidList';
			if (!isNil(this.$route.query.back)) {
				backRoute = this.$route.query.back;
			}
			this.$router.push({ name: backRoute });
		}
	},
	mounted() {
		this.initialize();
	}
}
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// PendingBidForm.vue?50d0baf6